import { HgSupportedLanguage } from '@smg-real-estate/domus';
import { type GeoLocation, type Locale } from '@smg-real-estate/geo-libs';
import {
  createMemoryHistory,
  createRouter,
  createWebHistory,
  type LocationQuery,
  type RouteLocation,
  type RouteLocationNormalized,
  type RouteLocationRaw,
  type Router,
  type RouteRecordRaw,
} from 'vue-router';

import { cityLocale, municipalityLocale } from '@/helpers/geo/geoUrlHelper';
import { isServer } from '@/helpers/platform';
import { type UrlParams } from '@/stores/types';
import { type LocalizedUrls } from '@/stores/useGeoStore';

const BASE_URL = import.meta.env.VUE_PLATFORM_BASE_URL;
const VIEW_NAME_PREFIX = 'Market Atlas';

export const localizedHomepagePaths = {
  [HgSupportedLanguage.DE]: '/de/immobilienpreise-pro-m2/:location?',
  [HgSupportedLanguage.FR]: '/fr/prix-immobilier-m2/:location?',
  [HgSupportedLanguage.IT]: '/it/prezzi-immobili-m2/:location?',
  [HgSupportedLanguage.EN]: '/en/property-prices-m2/:location?',
};

export const getBasePathWithLocation = (language: HgSupportedLanguage, location?: string): string =>
  localizedHomepagePaths[language].replace(/\/:location\??/g, location ? `/${location}` : '');

export function getSEORoutes(locations: GeoLocation[]): Record<Locale, LocalizedUrls> {
  return locations?.reduce(
    (acc, location) => {
      Object.entries(location.urlNames).forEach(([key, value]) => {
        const locale = key as Locale;
        if (!acc[locale]) {
          acc[locale] = [];
        }
        let url = getBasePathWithLocation(key as HgSupportedLanguage, value);
        url = url.replace(municipalityLocale[locale], cityLocale[locale]);
        const name = location.names[key as HgSupportedLanguage][0];
        acc[locale].push({
          name: name,
          url: `${BASE_URL}${url}`,
        });
      });
      return acc;
    },
    {} as Record<Locale, LocalizedUrls>,
  );
}

export function getLanguageSwitchLink(
  route: RouteLocationNormalized,
  language: HgSupportedLanguage,
): RouteLocationNormalized {
  return {
    ...route,
    params: {
      ...route.params,
      locale: language,
    },
  };
}

export function getLocalizedPageRoute(
  route: RouteLocationNormalized,
  language: HgSupportedLanguage,
  localizedUrlParams?: UrlParams,
): RouteLocationRaw {
  const hasLocalizedParams = localizedUrlParams !== undefined;

  const params = { ...route.params };
  const query: LocationQuery = { ...route.query };

  if (hasLocalizedParams) {
    const location = localizedUrlParams.param || undefined;
    if (location) {
      params.location = location;
    } else {
      delete params.location;
    }

    if (localizedUrlParams.zipCity) {
      query.zipCity = localizedUrlParams.zipCity;
    } else {
      delete query.zipCity;
    }
  }

  return {
    name: `property-prices-page-${language}`,
    query,
    params,
  };
}

export const routes: RouteRecordRaw[] = [
  ...Object.entries(localizedHomepagePaths).map(([locale = HgSupportedLanguage.DE, path]) => ({
    path: path,
    name: `property-prices-page-${locale}`,
    component: () => import('@/views/property-prices-page/PropertyPricesEntryPage.vue'),
    meta: {
      locale,
      viewName: `${VIEW_NAME_PREFIX} Property Prices Page`,
      getLanguageSwitchLink: getLocalizedPageRoute,
    },
  })),
  {
    path: '/:locale/:pageNotFound*',
    redirect(to: RouteLocation): RouteLocationRaw {
      return {
        name: `property-prices-page-${to.params.locale}`,
      };
    },
  },
];

const _createRouter = (): Router => {
  return createRouter({
    history: isServer()
      ? createMemoryHistory(import.meta.env.VUE_BASE_PATH)
      : createWebHistory(import.meta.env.VUE_BASE_PATH),
    routes,
  });
};
export default _createRouter;
